import React, { useCallback, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../context/AuthContext";
import { apiUrl, GOOGLE_CLIENT_ID } from "../utils/api";

const LoginForm = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [googleReady, setGoogleReady] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { usuario, authLoading, setUsuario } = useContext(AuthContext);

  const completarIngreso = useCallback(async (token) => {
    localStorage.setItem("token", token);

    const me = await axios.get(apiUrl('/auth/users/me/'), {
      headers: { Authorization: `Token ${token}` },
    });

    setUsuario(me.data);

    const rutaDestino = location.state?.from?.pathname;

    if (rutaDestino && rutaDestino !== "/login") {
      navigate(rutaDestino, { replace: true });
      return;
    }

    if (me.data.dashboard_autorizado) {
      navigate("/dashboard-casos", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  }, [location.state, navigate, setUsuario]);

  useEffect(() => {
    if (localStorage.getItem("logout_success") === "true") {
      toast.info("Sesion finalizada correctamente");
      localStorage.removeItem("logout_success");
    }
  }, []);

  useEffect(() => {
    if (authLoading || !usuario) {
      return;
    }

    if (usuario.dashboard_autorizado) {
      navigate("/dashboard-casos", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  }, [authLoading, navigate, usuario]);

  useEffect(() => {
    let cancelled = false;

    const initializeGoogle = () => {
      if (cancelled || !window.google?.accounts?.id) {
        return false;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          if (!response.credential) {
            toast.error("Google no devolvio una credencial valida");
            return;
          }

          setGoogleLoading(true);
          try {
            const res = await axios.post(apiUrl('/auth/google/login/'), {
              credential: response.credential,
            });

            await completarIngreso(res.data.auth_token);
            toast.success("Inicio de sesión con Google exitoso");
          } catch (error) {
            console.error("Error al iniciar sesión con Google:", error);
            const mensaje = error.response?.data?.detail || "No se pudo iniciar sesión con Google";
            toast.error(mensaje);
          } finally {
            setGoogleLoading(false);
          }
        },
      });

      const buttonContainer = document.getElementById("google-signin-button");
      if (buttonContainer) {
        buttonContainer.innerHTML = "";
        window.google.accounts.id.renderButton(buttonContainer, {
          theme: "outline",
          size: "large",
          shape: "pill",
          text: "signin_with",
          width: "320",
        });
        setGoogleReady(true);
      }

      return true;
    };

    if (initializeGoogle()) {
      return () => {
        cancelled = true;
      };
    }

    const intervalId = window.setInterval(() => {
      if (initializeGoogle()) {
        window.clearInterval(intervalId);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [completarIngreso]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(apiUrl('/auth/token/login/'), {
        username,
        password,
      });

      await completarIngreso(res.data.auth_token);
      toast.success("Inicio de sesion exitoso");
    } catch (error) {
      console.error("Error al iniciar sesion:", error);
      toast.error("Credenciales invalidas");
    }
  };

  return (
    <section className="container page-shell page-shell--narrow">
      <div className="surface-card login-card">
        <span className="section-kicker">Acceso seguro</span>
        <h3 className="page-title mb-2">Iniciar sesion</h3>
        <p className="page-subtitle">Ingresa con tus credenciales o con Google para continuar trabajando.</p>
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="mb-3">
            <label className="form-label">Usuario</label>
            <input
              type="text"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div className="mb-4">
            <label className="form-label">Contraseña</label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn btn-brand w-100">Ingresar</button>
        </form>

        <div className="login-divider">
          <span>o</span>
        </div>

        <div className="login-google-section">
          <div id="google-signin-button" className="login-google-button" />
          {!googleReady && <p className="login-google-hint mb-0">Cargando acceso con Google...</p>}
          {googleLoading && <p className="login-google-hint mb-0">Validando cuenta de Google...</p>}
          <p className="login-google-hint mb-0">
            Solo se permite el acceso con emails que ya tengan un usuario creado por el administrador.
          </p>
        </div>
      </div>
      <ToastContainer />
    </section>
  );
};

export default LoginForm;
